import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type {
    GetPayrollRunResponse,
    GetPayrollRunsResponse,
} from '@/api/handlers/resources/salaries/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { SalaryQueryKeys } from './salaryQueryKeys';

/**
 * Committed payroll runs, newest period first. A preview is not a run — and a
 * run in this list may still carry a DRAFT entry, which is what its
 * `entryStatus` says.
 */
export const usePayrollRunsQuery = (enabled = true) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetPayrollRunsResponse>>({
        queryKey: SalaryQueryKeys.runs(),
        queryFn: () => apiClient.salaries.getPayrollRuns(),
        staleTime: 60_000,
        enabled,
    });
};

/**
 * One run's per-employee breakdown, by **journal entry id** — the run's only
 * identity. Disabled while the detail drawer is closed (`journalEntryId ===
 * null`).
 */
export const usePayrollRunQuery = (journalEntryId: string | null) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<GetPayrollRunResponse>>({
        queryKey: SalaryQueryKeys.run(journalEntryId ?? 'none'),
        queryFn: () =>
            apiClient.salaries.getPayrollRun(journalEntryId as string),
        enabled: journalEntryId !== null,
    });
};
